import {
  StudioMember,
  useArchiveMember,
  useRestoreMember,
} from '@/features/studio/hooks/useStudio';
import { Archive, ArchiveRestore, User } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'sonner';

interface Props {
  member: StudioMember;
  onViewProfile: (userId: string) => void;
}

export function MemberCard({ member, onViewProfile }: Props) {
  const archive = useArchiveMember();
  const restore = useRestoreMember();
  const isArchived = member.status === 'archived';
  const joinedDate = member.joined_at
    ? format(new Date(member.joined_at), 'd MMM yyyy', { locale: fr })
    : 'Pas encore rejoint';

  const handleArchive = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await archive.mutateAsync(member.id);
      toast.success(`${member.display_name ?? 'Artiste'} suspendu.`);
    } catch {
      toast.error('Impossible de suspendre ce membre.');
    }
  };

  const handleRestore = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await restore.mutateAsync(member.id);
      toast.success(`${member.display_name ?? 'Artiste'} réactivé.`);
    } catch {
      toast.error('Impossible de réactiver ce membre.');
    }
  };

  return (
    <div
      onClick={() => onViewProfile(member.user_id)}
      className={`card-elevated p-4 flex items-center gap-4 cursor-pointer hover:bg-secondary/30 transition-colors ${
        isArchived ? 'opacity-50' : ''
      }`}
    >
      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
        <User size={18} className="text-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-semibold text-sm truncate">
            {member.display_name ?? member.email ?? member.user_id.slice(0, 8)}
          </p>
          <span
            className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${
              member.role === 'guest'
                ? 'bg-amber-500/10 text-amber-600'
                : 'bg-primary/10 text-primary'
            }`}
          >
            {member.role === 'guest' ? 'Guest' : 'Résident'}
          </span>
          {isArchived && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-destructive/10 text-destructive font-bold uppercase">
              Suspendu
            </span>
          )}
        </div>
        <p className="text-xs text-muted-foreground">{joinedDate}</p>
      </div>
      <div className="shrink-0">
        {isArchived ? (
          <button
            onClick={handleRestore}
            disabled={restore.isPending}
            className="p-2 rounded-lg text-emerald-600 hover:bg-emerald-500/10 transition-colors"
            title="Réactiver"
          >
            <ArchiveRestore size={16} />
          </button>
        ) : (
          <button
            onClick={handleArchive}
            disabled={archive.isPending}
            className="p-2 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
            title="Suspendre"
          >
            <Archive size={16} />
          </button>
        )}
      </div>
    </div>
  );
}
